import {
  LayoutDashboard,
  Package,
  Building2,
} from "lucide-react";
import { SectionTitle, Sub, RefLink, P, ConnectsTo } from "./GuideHelpers";
import { PageCard } from "./PageCard";

export const SectionDashboardProducts = () => (
  <>
    <SectionTitle id="dashboard" scope="brand">Brand Overview</SectionTitle>
    <PageCard icon={LayoutDashboard} title="Brand Overview" route="/" purpose="Brand-wide performance overview — the first thing you see when you log in.">
      <P>
        The Dashboard aggregates data from all other pages into a single view. It shows KPIs
        (total keywords tracked, top-10 keywords, average rank), a sales-vs-rank correlation
        chart, biggest rank movers, brand signals summary, product overview cards, and recent
        landscape activity.
      </P>
      <Sub>What to look for</Sub>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li><strong className="text-foreground">KPI trends</strong> — Are your top-10 keywords growing? Is average rank improving?</li>
        <li><strong className="text-foreground">Biggest gains & declines</strong> — Which keywords moved the most? Investigate declines immediately.</li>
        <li><strong className="text-foreground">Correlation chart</strong> — Does improving rank correlate with higher sales?</li>
        <li><strong className="text-foreground">Activity feed</strong> — Recent actions across the platform.</li>
      </ul>
      <ConnectsTo pages={["Products", "Brand Signals", "Rank Tracker", "Landscapes"]} />
    </PageCard>

    <SectionTitle id="brand-context" scope="brand">Brand Context</SectionTitle>
    <PageCard icon={Building2} title="Brand Context" route="/brand-context" purpose="The brand's knowledge base — documents, notes, and brand-level prohibited keywords that ground every AI feature in Signal.">
      <P>
        Brand Context is where you teach Signal about the brand. Anything stored here — uploaded
        documents, written notes, and the brand-level prohibited keyword list — is fed into the
        prompts used by SEO Insights, Search Terms generation, A/B Test drafting, and the Content
        Scorecard. The richer this page, the more on-brand and accurate the AI output.
      </P>
      <Sub>Knowledge entries</Sub>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li><strong className="text-foreground">Documents</strong> — Upload brand books, voice guides, customer research, competitive intel, campaign learnings, or product specs.</li>
        <li><strong className="text-foreground">Notes</strong> — Capture inline notes for things that don't warrant a full doc.</li>
        <li><strong className="text-foreground">Categories</strong> — Each entry is tagged (Brand Voice, Customer Insights, Competitive Intel, Product Knowledge, Campaign Learnings, Other) so the AI can pull the right context for the task.</li>
      </ul>
      <Sub>Brand-level prohibited keywords</Sub>
      <P>
        The right-hand panel manages this brand's prohibited keyword list. Terms added here are
        blocked across every AI surface for this brand only — Search Terms generation, SEO Insights
        recommendations, A/B Test copy, and Scorecard suggestions all respect this list in addition
        to the global list. Use it for competitor names, retired SKUs, claims that legal has flagged
        for this brand specifically, or anything that should never appear in this brand's listings
        but might be fine elsewhere.
      </P>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li>Add keywords one at a time, paste a bulk list, or upload a CSV/TXT file.</li>
        <li>Entries are normalized to lowercase and de-duplicated automatically.</li>
        <li>For terms that should be blocked across <em>all</em> brands, use the global <RefLink to="prohibited-keywords">Prohibited Keywords</RefLink> page instead.</li>
        <li>For terms that should only be blocked on a single product, use the product-level prohibited keywords (kebab menu on Product Detail).</li>
      </ul>
      <ConnectsTo pages={["Prohibited Keywords", "Search Terms", "SEO Insights", "A/B Tests", "Content Scorecard"]} />
    </PageCard>

    <SectionTitle id="products" scope="brand">Products Hub</SectionTitle>
    <PageCard icon={Package} title="Products" route="/products" purpose="Central catalog of all Hero ASINs for the selected brand.">
      <P>
        This is your product portfolio. Each card represents one Hero ASIN. Click a product to
        open its Product Detail page, where all product-level tools live.
      </P>
      <Sub>How to use it</Sub>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li>Click <strong className="text-foreground">Add Hero ASIN</strong> to add new products to track.</li>
        <li>Click any product card to drill into its detail page.</li>
        <li>Products are scoped to the selected brand — switch brands in the sidebar to see different products.</li>
      </ul>
      <ConnectsTo pages={["Product Detail", "Dashboard"]} />
    </PageCard>

    <SectionTitle id="product-detail" scope="brand">Product Detail</SectionTitle>
    <PageCard icon={Package} title="Product Detail" route="/products/:productId" purpose="The command center for a single product — six tabs of product-level tools.">
      <P>
        When you click a product from the Products Hub, you land here. The header shows the product
        image, title, ASIN, and a quick hero-vs-competitor comparison (price, rating, keywords, page 1 presence).
        Below the header, six tabs give you access to all product-level functionality: SEO Insights,
        Keywords, Landscapes, A/B Tests, Search Terms, and Content Scorecard.
      </P>
    </PageCard>
  </>
);
